import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect'

import { applyThousandSeperator } from '../../util';

import {
    setPaginationSizeValue
} from '@om/redux/actions/priceTable/table';

import {
    getPaginationRanges,
    getPaginationTableSize
} from '@om/redux/selectors/priceTable/table';
import PaginationBlock from '../PaginationBlock';
import HowToDownloadModal from '../HowToDownloadModal';
import {
    OmPtTableContent,
    OmTable,
    OmIcon,
} from '@om/component-library-react';

const PriceTableContent = ({
    slot,
    id,
    fundData,
    hasGraph,
    hasPagination,
}) => {
    const dispatch = useDispatch();

    const {
        paginationRanges,
        paginationTableSize,
    } = useSelector(
        createStructuredSelector({
            paginationRanges: getPaginationRanges,
            paginationTableSize: getPaginationTableSize,
        })
    );

    const paginatedFunds = (() => {
        return fundData.slice(0, paginationTableSize);
    })();

    const numberOfColumnsDesktop = (() => {
        return hasGraph ? 6 : 5;
    })();

    const numberOfRowsMobile = (() => {
        return hasGraph ? 5 : 4;
    })();

    const countryCurrencyCode = (() => {
        if (process.env.GATSBY_COUNTRY_SELECTOR === 'za' || process.env.GATSBY_COUNTRY_SELECTOR === 'ut') {
            return 'ZAR'
        } else if (process.env.GATSBY_COUNTRY_SELECTOR === 'na') {
            return 'N$'
        }
    })();

    const currentPaginatedValue = (() => {
        const maxNumberOfRecords = fundData.length;
        return paginationTableSize > maxNumberOfRecords ? maxNumberOfRecords : paginationTableSize;
    })();

    const paginationDropDownOptions = (() => {
        const ranges = paginationRanges.filter(value => value < fundData.length)
            .map(value => ({ label: value, value: value }));

        return [...ranges, { label: 'All', value: fundData.length }];
    })();

    const dateFormatter = (jsonDate) => {
        let date = new Date(jsonDate);
        return `${('0' + date.getDate()).slice(-2)} ${date.toLocaleString('default', { month: 'short' })}`
    }

    const determineIconBasedOnMovement = (Movement) => {
        const value = parseFloat(Movement);
        if (value > 0) {
            return 'pt_price_increase';
        } else if (value < 0) {
            return 'pt_price_decrease';
        } else {
            return ' ';
        }
    }

    const onSelectEventFunction = (event) => {
        dispatch(setPaginationSizeValue(parseInt(event.detail.id)));
        setTimeout(() => {
            const element = document.getElementById('full-range-table');
            if (element) {
                if (event.detail.id < paginatedFunds.length) {
                    const y = element.getBoundingClientRect().top + window.pageYOffset - 240;
                    window.scrollTo({ top: y });
                }
            }
        }, 80);
    }

    const buildPdfDownloadLink = (fundShortName) => {
        if (fundShortName) {
            if (process.env.GATSBY_COUNTRY_SELECTOR === 'za' || process.env.GATSBY_COUNTRY_SELECTOR === 'ut') {
                return `/docs/default-source/unit-trust/ut08factsheets/${fundShortName}.pdf`;
            } else if (process.env.GATSBY_COUNTRY_SELECTOR === 'na') {
                return `/om-docs/${fundShortName}.pdf`;
            }
        }
        return '';
    }

    const buildGraphLink = (fundId) => {
        if (fundId) {
            return `/individual/funds/price-graph?fundId=${fundId}`;
        }
        return '';
    }

    return (
        <OmPtTableContent
            id={id}
            slot={slot}
        >
            <div slot="price-table">
                <div className="mobile-table-heading">
                    <h6 className="no-top-margin no-bottom-margin">Old Mutual Fund Name</h6>
                </div>
                <OmTable
                    key={paginatedFunds.length}
                    theme="theme-default"
                    tableColumnsDesktop={numberOfColumnsDesktop}
                    tableHeaderRowsDesktop={1}
                    tableBodyRowsDesktop={paginatedFunds.length}

                    tablesMobile={paginatedFunds.length}
                    tableHeaderRowsMobile={1}
                    tableBodyColumnsMobile={2}
                    tableBodyRowsMobile={numberOfRowsMobile}
                >

                    <h6 slot="desktop-table-header-row-1-cell-1"><strong>OLD MUTUAL FUND NAME</strong></h6>
                    <h6 slot="desktop-table-header-row-1-cell-2"><strong>DATE</strong></h6>
                    <h6 slot="desktop-table-header-row-1-cell-3"><strong>PRICE (NAV)</strong></h6>
                    <h6 slot="desktop-table-header-row-1-cell-4"><strong>YIELD %</strong></h6>
                    {hasGraph
                        ? <>
                            <h6 slot="desktop-table-header-row-1-cell-5"><strong>GRAPH</strong></h6>
                            <h6 slot="desktop-table-header-row-1-cell-6"><strong>STATUS</strong></h6>
                        </>
                        : <h6 slot="desktop-table-header-row-1-cell-5"><strong>STATUS</strong></h6>}


                    {paginatedFunds && paginatedFunds.map((fund, index) => {
                        const fundPosition = index + 1;
                        return (
                            <React.Fragment key={index}>
                                {
                                    fund.FundShortName
                                        ? <a href={buildPdfDownloadLink(fund.FundShortName)} target="_blank" slot={`desktop-table-body-row-${fundPosition}-cell-1`} rel="noreferrer">{fund.FundName}</a>
                                        : <h6 slot={`desktop-table-body-row-${fundPosition}-cell-1`}>{fund.FundName}</h6>
                                }
                                <h6 className="medium small" slot={`desktop-table-body-row-${fundPosition}-cell-2`}>{dateFormatter(fund.PriceDate)}</h6>
                                <h6 className="medium small" slot={`desktop-table-body-row-${fundPosition}-cell-3`}>
                                    <span>{countryCurrencyCode}</span>{applyThousandSeperator(parseFloat(fund.SellPrice).toFixed(2))}<span>
                                        <OmIcon height="24" width="24" iconName={determineIconBasedOnMovement(fund.Movement)} />
                                    </span>
                                </h6>
                                <h6 className="medium small" slot={`desktop-table-body-row-${fundPosition}-cell-4`}>{fund.FundYield}</h6>
                                {hasGraph
                                    ? <>
                                        <a href={buildGraphLink(fund.FundId)} target="_blank" slot={`desktop-table-body-row-${fundPosition}-cell-5`} rel="noreferrer">
                                            <OmIcon height="24" width="24" iconName="arrow_open" />
                                        </a>
                                        <h6 className="medium small" slot={`desktop-table-body-row-${fundPosition}-cell-6`}>{fund.FundStatus}</h6>
                                    </>
                                    : <h6 className="medium small" slot={`desktop-table-body-row-${fundPosition}-cell-5`}>{fund.FundStatus}</h6>}


                                {
                                    fund.FundShortName
                                        ? <a href={buildPdfDownloadLink(fund.FundShortName)} slot={`mobile-table-${fundPosition}-header-row-1-cell-1`}>{fund.FundName}</a>
                                        : <h6 slot={`mobile-table-${fundPosition}-header-row-1-cell-1`}>{fund.FundName}</h6>
                                }

                                <h6 slot={`mobile-table-${fundPosition}-body-row-1-cell-1`}><strong>DATE</strong></h6>
                                <p slot={`mobile-table-${fundPosition}-body-row-1-cell-2`}>{dateFormatter(fund.PriceDate)}</p>

                                <h6 slot={`mobile-table-${fundPosition}-body-row-2-cell-1`}><strong>PRICE (NAV)</strong></h6>
                                <p slot={`mobile-table-${fundPosition}-body-row-2-cell-2`}>
                                    {`${countryCurrencyCode} ${applyThousandSeperator(parseFloat(fund.SellPrice).toFixed(2))}`}
                                    <span>
                                        <OmIcon height="24" width="24" iconName={determineIconBasedOnMovement(fund.Movement)} />
                                    </span>
                                </p>

                                <h6 slot={`mobile-table-${fundPosition}-body-row-3-cell-1`}><strong>YIELD %</strong></h6>
                                <p slot={`mobile-table-${fundPosition}-body-row-3-cell-2`}>{fund.FundYield}</p>

                                {hasGraph
                                    ? <>
                                        <h6 slot={`mobile-table-${fundPosition}-body-row-4-cell-1`}><strong>GRAPH</strong></h6>
                                        <a href={buildGraphLink(fund.FundId)} target="_blank" slot={`mobile-table-${fundPosition}-body-row-4-cell-2`} rel="noreferrer">
                                            <OmIcon height="24" width="24" icon-name="arrow_open" />
                                        </a>

                                        <h6 slot={`mobile-table-${fundPosition}-body-row-5-cell-1`}><strong>STATUS</strong></h6>
                                        <p slot={`mobile-table-${fundPosition}-body-row-5-cell-2`}>{fund.FundStatus}</p>
                                    </>
                                    : <>
                                        <h6 slot={`mobile-table-${fundPosition}-body-row-4-cell-1`}><strong>STATUS</strong></h6>
                                        <p slot={`mobile-table-${fundPosition}-body-row-4-cell-2`}>{fund.FundStatus}</p>
                                    </>}


                            </React.Fragment>
                        );
                    })}
                </OmTable>
                {paginatedFunds.length === 0
                    && <div className="no-funds-block">
                        <h5 className="no-bottom-margin no-top-margin">No funds to show</h5>
                        <h6 className="no-bottom-margin no-top-margin">Try another date</h6>
                    </div>}
                <HowToDownloadModal overlayName='price-table-how-to-download-modal'/>
            </div>
            {hasPagination && paginatedFunds.length > 0 &&
                <PaginationBlock
                    slot="pagination-block"
                    options={paginationDropDownOptions}
                    currentPaginatedValue={currentPaginatedValue}
                    numberOfRecords={fundData.length}
                    onSelectEventFunction={onSelectEventFunction}
                    showPrintButton={true}
                    openingOverlayModal='price-table-how-to-download-modal'
                />
            }
        </OmPtTableContent>
    )
}

export default PriceTableContent;
